import { z } from "zod/v3";
import { generatedImagePath } from "./image-generation";
import { fontFamilySchema, fontUrlSchema } from "./fonts";
import {
  defaultVector,
  vectorSchema,
  strokeCapSchema,
  strokeJoinSchema,
} from "./vector";

export const elementType = z.enum([
  "frame",
  "rectangle",
  "ellipse",
  "text",
  "button",
  "image",
  "path",
]);
export const MAX_ELEMENT_BLUR = 200;
export const ELEMENT_BLUR_DESCRIPTION =
  "Gaussian layer blur in design pixels (0–200). Blurs the element and its contents, not the background behind it. Set 0 to remove blur.";
export const elementSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().min(1).max(120),
  type: elementType,
  parentId: z.string().max(100).nullable().default(null),
  // Root elements belong to a page; descendants inherit their frame's page.
  pageId: z.string().min(1).max(100).optional(),
  x: z.number().finite().min(-20000).max(20000).default(0),
  y: z.number().finite().min(-20000).max(20000).default(0),
  width: z.number().finite().min(1).max(10000).default(200),
  height: z.number().finite().min(1).max(10000).default(100),
  fill: z
    .string()
    .regex(
      /^(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|transparent)$/,
    )
    .default("#ffffff"),
  color: z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/)
    .default("#222222"),
  stroke: z
    .string()
    .regex(
      /^(#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|transparent)$/,
    )
    .default("transparent"),
  strokeWidth: z.number().min(0).max(40).default(1),
  radius: z.number().min(0).max(1000).default(0),
  opacity: z.number().min(0).max(1).default(1),
  blur: z.number().finite().min(0).max(MAX_ELEMENT_BLUR).default(0)
    .describe(ELEMENT_BLUR_DESCRIPTION),
  rotation: z.number().min(-360).max(360).default(0),
  text: z.string().max(12000).default(""),
  fontSize: z.number().min(6).max(400).default(16),
  fontWeight: z.number().int().min(100).max(900).default(400),
  fontFamily: fontFamilySchema,
  fontUrl: fontUrlSchema,
  textAlign: z.enum(["left", "center", "right"]).default("left"),
  src: z
    .string()
    .max(2000)
    .refine((v) => !v || /^https:\/\//i.test(v) || generatedImagePath.test(v), "Use an HTTPS URL or a saved generated-image path")
    .default(""),
  hidden: z.boolean().default(false),
  locked: z.boolean().default(false),
  vector: vectorSchema.optional(),
  strokeCap: strokeCapSchema.default("round"),
  strokeJoin: strokeJoinSchema.default("round"),
});
export const createElementSchema = elementSchema
  .omit({ id: true })
  .partial()
  .required({ type: true });
export const patchElementSchema = elementSchema.omit({ id: true }).partial();
export const operationSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("add"),
    element: elementSchema.partial().required({ type: true }),
  }),
  z.object({
    op: z.literal("update"),
    id: z.string(),
    changes: patchElementSchema,
  }),
  z.object({ op: z.literal("delete"), id: z.string() }),
  z.object({
    op: z.literal("reorder"),
    id: z.string(),
    index: z.number().int().min(0),
  }),
]);
export const batchSchema = z.object({
  operations: z.array(operationSchema).min(1).max(200),
  revision: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export type DesignElement = z.infer<typeof elementSchema>;
export type ElementType = DesignElement["type"];
export type Operation = z.infer<typeof operationSchema>;
export type Activity = {
  id: string;
  actor: string;
  message: string;
  at: string;
};
export type DesignReference = {
  id: string;
  sourceProjectId: string | null;
  notes: string;
  addedAt: string;
};
export type ReferenceSummary = DesignReference & {
  name: string;
  url: string | null;
  available: boolean;
  sourceRevision?: number;
  elementCount?: number;
  pages?: DesignPage[];
  frames?: Array<Pick<DesignElement, "id" | "name" | "width" | "height">>;
};
export type ReferenceFile = ReferenceSummary & {
  document: Pick<Design, "id" | "name" | "revision" | "elements" | "pages">;
};
export type Design = {
  id: string;
  name: string;
  revision: number;
  elements: DesignElement[];
  pages?: DesignPage[];
  references?: DesignReference[];
  activity: Activity[];
  createdAt: string;
  updatedAt: string;
};
export const designPageSchema = z.object({
  id: z.string().min(1).max(100),
  name: z.string().trim().min(1).max(120),
});
export type DesignPage = z.infer<typeof designPageSchema>;
export const pageEditSchema = z.discriminatedUnion("op", [
  z.object({ op: z.literal("add"), page: designPageSchema }),
  z.object({ op: z.literal("rename"), id: z.string(), name: z.string().trim().min(1).max(120) }),
  z.object({ op: z.literal("delete"), id: z.string() }),
]);
export type PageEdit = z.infer<typeof pageEditSchema>;
export function designPages(doc: Pick<Design, "pages">): DesignPage[] {
  return doc.pages?.length ? doc.pages : [{ id: "page-1", name: "Page 1" }];
}
export function elementPageId(doc: Pick<Design, "pages" | "elements">, element: DesignElement): string {
  let root = element;
  const seen = new Set<string>();
  while (root.parentId && !seen.has(root.id)) {
    seen.add(root.id);
    const parent = doc.elements.find((e) => e.id === root.parentId);
    if (!parent) break;
    root = parent;
  }
  return root.pageId || designPages(doc)[0].id;
}
export function pageElements(doc: Pick<Design, "pages" | "elements">, pageId: string) {
  return doc.elements.filter((element) => elementPageId(doc, element) === pageId);
}
export function validatePages(doc: Pick<Design, "pages" | "elements">) {
  const pages = z.array(designPageSchema).min(1).max(100).parse(designPages(doc));
  const ids = new Set(pages.map((page) => page.id));
  if (ids.size !== pages.length) throw Error("Page IDs must be unique.");
  for (const element of doc.elements) {
    const pageId = elementPageId(doc, element);
    if (!ids.has(pageId)) throw Error("Element page does not exist.");
    if (element.pageId && element.pageId !== pageId)
      throw Error("Elements must belong to the same page as their parent frame.");
  }
}
export type ProjectSummary = Pick<
  Design,
  "id" | "name" | "revision" | "updatedAt"
>;

export function makeElement(
  type: ElementType,
  values: Partial<DesignElement> = {},
): DesignElement {
  return elementSchema.parse({
    id: crypto.randomUUID(),
    name: type[0].toUpperCase() + type.slice(1),
    type,
    ...(type === "text"
      ? {
          width: 260,
          height: 48,
          fill: "transparent",
          text: "Your next idea",
          fontSize: 28,
          fontWeight: 400,
        }
      : {}),
    ...(type === "frame" ? { width: 800, height: 600, fill: "#ffffff" } : {}),
    ...(type === "rectangle"
      ? { width: 220, height: 150, fill: "#e3e3e3", radius: 12 }
      : {}),
    ...(type === "ellipse" ? { width: 120, height: 120, fill: "#e3e3e3" } : {}),
    ...(type === "path"
      ? {
          width: 220,
          height: 160,
          fill: "transparent",
          stroke: "#303030",
          strokeWidth: 2,
          vector: defaultVector(),
        }
      : {}),
    ...(type === "button"
      ? {
          width: 160,
          height: 44,
          fill: "#2a2a2a",
          color: "#ffffff",
          text: "Get started",
          fontWeight: 600,
          radius: 8,
          textAlign: "center",
        }
      : {}),
    ...values,
  });
}
export function descendants(
  elements: DesignElement[],
  id: string,
): Set<string> {
  const ids = new Set([id]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const e of elements) {
      if (e.parentId && ids.has(e.parentId) && !ids.has(e.id)) {
        ids.add(e.id);
        changed = true;
      }
    }
  }
  return ids;
}
export function validateTree(elements: DesignElement[]) {
  const map = new Map(elements.map((e) => [e.id, e]));
  if (map.size !== elements.length) throw Error("Element IDs must be unique.");
  for (const e of elements) {
    if (e.type === "path" && !e.vector)
      throw Error("Vector paths require path geometry.");
    let p = e.parentId;
    const visited = new Set([e.id]);
    while (p) {
      if (visited.has(p)) throw Error("Elements cannot contain themselves.");
      visited.add(p);
      const parent = map.get(p);
      if (!parent) throw Error("Parent element does not exist.");
      if (parent.type !== "frame")
        throw Error("Only frames can contain elements.");
      p = parent.parentId;
    }
  }
}
export function applyOperations(
  elements: DesignElement[],
  operations: Operation[],
): DesignElement[] {
  let result = elements.map((e) => ({ ...e }));
  for (const operation of operations) {
    if (operation.op === "add") {
      result.push(makeElement(operation.element.type, operation.element));
      continue;
    }
    const index = result.findIndex((e) => e.id === operation.id);
    if (index < 0) throw Error(`Element ${operation.id} does not exist.`);
    if (operation.op === "update")
      result[index] = elementSchema.parse({
        ...result[index],
        ...operation.changes,
      });
    if (operation.op === "delete") {
      const ids = descendants(result, operation.id);
      result = result.filter((e) => !ids.has(e.id));
    }
    if (operation.op === "reorder") {
      const [e] = result.splice(index, 1);
      result.splice(Math.min(operation.index, result.length), 0, e);
    }
  }
  validateTree(result);
  return result;
}
export function elementPosition(
  element: DesignElement,
  elements: DesignElement[],
): { x: number; y: number } {
  let x = element.x,
    y = element.y,
    p = element.parentId;
  const seen = new Set<string>();
  while (p && !seen.has(p)) {
    seen.add(p);
    const parent = elements.find((e) => e.id === p);
    if (!parent) break;
    x += parent.x;
    y += parent.y;
    p = parent.parentId;
  }
  return { x, y };
}
